import { create } from 'zustand';
import { authorizedAPI } from '@/config/axios.config';
import { calculateArea, calculateRadius } from '@/utils/utm-geometry';

interface SelectionFeature {
  id: string;
  geometry: any;
}

interface ZoneGeometryMap {
  [zoneId: string]: any;
}

type DrawingElevation = {
  lowerBoundM?: number;
  lowerRef?: 'AGL' | 'AMSL';
  upperBoundM?: number;
  upperRef?: 'AGL' | 'AMSL';
};

const combineSelectionGeometries = (features: SelectionFeature[]): any => {
  if (features.length === 0) return null;
  if (features.length === 1) return features[0].geometry;
  return {
    type: 'GeometryCollection',
    geometries: features.map((feature) => feature.geometry),
  };
};

// Types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Category {
  id: string;
  name: string;
  defaultVisibleStatus: 'ALWAYS_VISIBLE' | 'VISIBLE_WHEN_CHECKED';
  description?: string;
  iconType: 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT';
  iconValue: string;
  iconValueDark?: string;
  iconColor?: string;
  iconBgColor?: string;
  style?: Record<string, any>;
  groups?: Group[];
}

export interface Group {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  defaultVisibleStatus: 'ALWAYS_VISIBLE' | 'VISIBLE_WHEN_CHECKED';
  description?: string;
  iconType: 'BUILTIN' | 'SVG' | 'IMAGE_URL' | 'S3_OBJECT';
  iconValue: string;
  iconValueDark?: string;
  iconColor?: string;
  iconBgColor?: string;
  style?: Record<string, any>;
  category?: Category;
}

export interface Zone {
  id: string;
  groupId: string;
  status: 'NOT_PUBLISHED' | 'PUBLISHED';
  zoneCode: string;
  zoneName: string;
  activeStart?: string;
  activeEnd?: string;
  description?: string;
  style?: Record<string, any>;
  geometry?: {
    id: string;
    geom: any;
    lowerBoundM?: number;
    lowerRef?: 'AGL' | 'AMSL';
    upperBoundM?: number;
    upperRef?: 'AGL' | 'AMSL';
  };
  impacts?: Impact[];
  group?: Group;
}

export interface Impact {
  id: string;
  name: string;
  type?: 'INTERUSSP';
  level: 'NOTAM' | 'AUTHORISATION' | 'NOTIFICATION' | 'NOTHING_TO_DO';
  linkType: 'CATEGORY' | 'GROUP' | 'ZONE';
  linkedIds?: string[];
  linkedEntities?: Array<Record<string, any>>;
  status: 'CHECKED' | 'UNCHECKED';
  intersectionRequirement: 'MUST_BE_WITHIN' | 'MUST_BE_OUTSIDE';
  triggerZones?: Array<{
    id: string;
    zoneCode: string;
    zoneName: string;
    groupId: string;
    group?: {
      id: string;
      code: string;
      name: string;
    };
  }>;
  description?: string;
  zones?: Zone[];
}

export interface IntersectionResult {
  zones: Zone[];
  impacts: Impact[];
}

interface UtmTaxonomyStore {
  // Data
  categories: Category[];
  groups: Group[];
  zones: Zone[];
  impacts: Impact[];

  // UI State
  activeGroups: Set<string>;
  hasUserCustomizedVisibility: boolean;
  expandedCategories: Set<string>;
  selectedZone: Zone | null;
  selectionResults: IntersectionResult | null;
  selectionMode: 'none' | 'radius' | 'polygon' | 'corridor';
  selectionFeatures: SelectionFeature[];
  activeSelectionFeatureId: string | null;
  selectionResultsByFeatureId: Record<string, IntersectionResult>;
  selectionElevationByFeatureId: Record<string, DrawingElevation>;
  intersectionZoneIdsByFeatureId: Record<string, string[]>;
  visibleIntersectionZoneIdsByFeatureId: Record<string, string[]>;
  hoveredZoneIds: string[];
  zoneGeometryById: ZoneGeometryMap;
  isLoading: boolean;
  basemapStyle: string;

  // Drawing State
  drawingElevation: DrawingElevation;
  corridorWidth: number;
  currentDrawingGeometry: any;
  currentDrawingArea: number;
  currentDrawingRadius: number | null;

  // Actions - Taxonomy
  fetchTaxonomy: () => Promise<void>;
  toggleGroup: (code: string, visible?: boolean) => void;
  expandCategory: (id: string, expanded?: boolean) => void;

  // Actions - Categories
  createCategory: (category: Partial<Category>) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;

  // Actions - Groups
  createGroup: (group: Partial<Group>) => Promise<void>;
  updateGroup: (id: string, updates: Partial<Group>) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;

  // Actions - Zones
  fetchZones: (filters?: { groupCode?: string; status?: string; activeNow?: boolean }) => Promise<void>;
  createZone: (zone: Partial<Zone>) => Promise<Zone>;
  updateZone: (id: string, updates: Partial<Zone>) => Promise<void>;
  publishZone: (id: string) => Promise<void>;
  unpublishZone: (id: string) => Promise<void>;
  deleteZone: (id: string) => Promise<void>;
  uploadGeometry: (zoneId: string, geojson: any) => Promise<void>;
  updateGeometryBounds: (
    zoneId: string,
    bounds: { lowerBoundM?: number; lowerRef?: 'AGL' | 'AMSL'; upperBoundM?: number; upperRef?: 'AGL' | 'AMSL' }
  ) => Promise<void>;
  selectZone: (zoneId: string | null) => void;

  // Actions - Impacts
  fetchImpacts: () => Promise<void>;
  createImpact: (impact: Partial<Impact>) => Promise<void>;
  updateImpact: (id: string, updates: Partial<Impact>) => Promise<void>;
  deleteImpact: (id: string) => Promise<void>;
  attachImpact: (zoneId: string, impactId: string) => Promise<void>;
  detachImpact: (zoneId: string, impactId: string) => Promise<void>;

  // Actions - Impact-Zone Linking
  fetchImpactZones: (
    impactId: string,
    filters?: { page?: number; limit?: number; search?: string; groupCode?: string; categoryId?: string; status?: string }
  ) => Promise<PaginatedResponse<Zone>>;
  fetchAvailableZones: (
    impactId: string,
    filters?: { page?: number; limit?: number; search?: string; groupCode?: string; categoryId?: string; status?: string }
  ) => Promise<PaginatedResponse<Zone>>;
  attachZoneToImpact: (impactId: string, zoneId: string) => Promise<void>;
  detachZoneFromImpact: (impactId: string, zoneId: string) => Promise<void>;

  // Actions - Analysis
  queryIntersections: (geometry: any, filters?: Record<string, any>) => Promise<IntersectionResult | null>;
  setSelectionMode: (mode: 'none' | 'radius' | 'polygon' | 'corridor') => void;
  addSelectionFeature: (geometry: any) => Promise<void>;
  setActiveSelectionFeature: (id: string | null) => void;
  removeActiveSelectionFeature: () => Promise<void>;
  setIntersectionZoneVisible: (zoneId: string, visible: boolean) => void;
  setHoveredZoneIds: (zoneIds: string[]) => void;
  ensureZoneGeometries: (zoneIds: string[]) => Promise<void>;
  clearSelection: () => void;

  // Actions - Drawing State
  setDrawingElevation: (elevation: Partial<DrawingElevation>) => void;
  setCorridorWidth: (width: number) => void;
  setCurrentDrawingGeometry: (geometry: any) => void;
  updateDrawingCalculations: (geometry: any) => void;

  // Utilities
  getEffectiveStyle: (groupCode: string) => Record<string, any>;
  setBasemapStyle: (style: string) => void;
}

export const useUtmTaxonomyStore = create<UtmTaxonomyStore>((set, get) => ({
  // Initial State
  categories: [],
  groups: [],
  zones: [],
  impacts: [],
  activeGroups: new Set(),
  hasUserCustomizedVisibility: false,
  expandedCategories: new Set(),
  selectedZone: null,
  selectionResults: null,
  selectionMode: 'none',
  selectionFeatures: [],
  activeSelectionFeatureId: null,
  selectionResultsByFeatureId: {},
  selectionElevationByFeatureId: {},
  intersectionZoneIdsByFeatureId: {},
  visibleIntersectionZoneIdsByFeatureId: {},
  hoveredZoneIds: [],
  zoneGeometryById: {},
  isLoading: false,
  basemapStyle: 'mapbox://styles/mapbox/streets-v12',

  // Drawing State Initial Values
  drawingElevation: {},
  corridorWidth: 100,
  currentDrawingGeometry: null,
  currentDrawingArea: 0,
  currentDrawingRadius: null,

  // Taxonomy
  fetchTaxonomy: async () => {
    set({ isLoading: true });
    try {
      const [categoriesRes, groupsRes] = await Promise.all([
        authorizedAPI.get('/utm/categories'),
        authorizedAPI.get('/utm/groups'),
      ]);
      const categories = categoriesRes.data as Category[];
      const groups = groupsRes.data as Group[];
      const state = get();
      let nextActiveGroups = state.activeGroups;

      // Apply defaults only until user manually changes checkbox states.
      if (!state.hasUserCustomizedVisibility) {
        const alwaysVisibleCategoryIds = new Set(
          categories
            .filter((category) => category.defaultVisibleStatus === 'ALWAYS_VISIBLE')
            .map((category) => category.id),
        );

        nextActiveGroups = new Set(
          groups
            .filter(
              (group) =>
                group.defaultVisibleStatus === 'ALWAYS_VISIBLE' ||
                alwaysVisibleCategoryIds.has(group.categoryId),
            )
            .map((group) => group.code),
        );
      }

      set({
        categories,
        groups,
        activeGroups: nextActiveGroups,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to fetch UTM taxonomy:', error);
      set({ isLoading: false });
    }
  },

  toggleGroup: (code: string, visible?: boolean) => {
    set((state) => {
      const newActive = new Set(state.activeGroups);
      const isCurrentlyActive = newActive.has(code);
      const shouldBeActive = visible !== undefined ? visible : !isCurrentlyActive;
      if (shouldBeActive) {
        newActive.add(code);
      } else {
        newActive.delete(code);
      }
      return { activeGroups: newActive, hasUserCustomizedVisibility: true };
    });
  },

  expandCategory: (id: string, expanded?: boolean) => {
    set((state) => {
      const newExpanded = new Set(state.expandedCategories);
      const isExpanded = newExpanded.has(id);
      const shouldExpand = expanded !== undefined ? expanded : !isExpanded;
      if (shouldExpand) {
        newExpanded.add(id);
      } else {
        newExpanded.delete(id);
      }
      return { expandedCategories: newExpanded };
    });
  },

  // Categories
  createCategory: async (category: Partial<Category>) => {
    set({ isLoading: true });
    try {
      await authorizedAPI.post('/utm/categories', category);
      await get().fetchTaxonomy();
    } catch (error) {
      console.error('Failed to create UTM category:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  updateCategory: async (id: string, updates: Partial<Category>) => {
    try {
      await authorizedAPI.patch(`/utm/categories/${id}`, updates);
      await get().fetchTaxonomy();
    } catch (error) {
      console.error('Failed to update UTM category:', error);
      throw error;
    }
  },

  deleteCategory: async (id: string) => {
    set({ isLoading: true });
    try {
      await authorizedAPI.delete(`/utm/categories/${id}`);
      await get().fetchTaxonomy();
    } catch (error) {
      console.error('Failed to delete UTM category:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  // Groups
  createGroup: async (group: Partial<Group>) => {
    set({ isLoading: true });
    try {
      await authorizedAPI.post('/utm/groups', group);
      await get().fetchTaxonomy();
    } catch (error) {
      console.error('Failed to create UTM group:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  updateGroup: async (id: string, updates: Partial<Group>) => {
    try {
      await authorizedAPI.patch(`/utm/groups/${id}`, updates);
      await get().fetchTaxonomy();
    } catch (error) {
      console.error('Failed to update UTM group:', error);
      throw error;
    }
  },

  deleteGroup: async (id: string) => {
    set({ isLoading: true });
    try {
      await authorizedAPI.delete(`/utm/groups/${id}`);
      await get().fetchTaxonomy();
    } catch (error) {
      console.error('Failed to delete UTM group:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  // Zones
  fetchZones: async (filters = {}) => {
    set({ isLoading: true });
    try {
      const params = new URLSearchParams();
      if (filters.groupCode) params.append('groupCode', filters.groupCode);
      if (filters.status) params.append('status', filters.status);
      if (filters.activeNow !== undefined) params.append('activeNow', String(filters.activeNow));

      const res = await authorizedAPI.get(`/utm/zones?${params.toString()}`);
      set({ zones: res.data.data, isLoading: false });
    } catch (error) {
      console.error('Failed to fetch UTM zones:', error);
      set({ isLoading: false });
    }
  },

  createZone: async (zone: Partial<Zone>) => {
    set({ isLoading: true });
    try {
      const response = await authorizedAPI.post('/utm/zones', zone);
      const createdZone = response.data;
      await get().fetchZones();
      return createdZone;
    } catch (error: any) {
      console.error('Failed to create UTM zone:', error);
      set({ isLoading: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.status === 500 && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.includes('already exists')) {
          throw new Error('A zone with this code already exists in the selected group. Please use a different zone code.');
        }
        throw new Error(detail);
      }
      throw new Error('Failed to create zone. Please try again.');
    }
  },

  updateZone: async (id: string, updates: Partial<Zone>) => {
    try {
      await authorizedAPI.patch(`/utm/zones/${id}`, updates);
      await get().fetchZones();
    } catch (error: any) {
      console.error('Failed to update UTM zone:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.status === 500 && error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw new Error('Failed to update zone. Please try again.');
    }
  },

  publishZone: async (id: string) => {
    try {
      await authorizedAPI.post(`/utm/zones/${id}/publish`);
      await get().fetchZones();
    } catch (error) {
      console.error('Failed to publish UTM zone:', error);
      throw error;
    }
  },

  unpublishZone: async (id: string) => {
    try {
      await authorizedAPI.post(`/utm/zones/${id}/unpublish`);
      await get().fetchZones();
    } catch (error) {
      console.error('Failed to unpublish UTM zone:', error);
      throw error;
    }
  },

  deleteZone: async (id: string) => {
    set({ isLoading: true });
    try {
      await authorizedAPI.delete(`/utm/zones/${id}`);
      await get().fetchZones();
    } catch (error) {
      console.error('Failed to delete UTM zone:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  uploadGeometry: async (zoneId: string, geojson: any) => {
    try {
      await authorizedAPI.put(`/utm/zones/${zoneId}/geometry`, geojson);
      await get().fetchZones();
    } catch (error: any) {
      console.error('Failed to upload UTM geometry:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to upload geometry. Please check the file format and try again.');
    }
  },

  updateGeometryBounds: async (zoneId: string, bounds) => {
    try {
      await authorizedAPI.patch(`/utm/zones/${zoneId}/geometry/bounds`, bounds);
      await get().fetchZones();
    } catch (error: any) {
      console.error('Failed to update UTM geometry bounds:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update altitude bounds. Please try again.');
    }
  },

  selectZone: (zoneId: string | null) => {
    if (!zoneId) {
      set({ selectedZone: null });
      return;
    }

    const { zones, selectionResults } = get();
    const zoneFromStore = zones.find((z) => z.id === zoneId);
    const zoneFromSelection = selectionResults?.zones?.find((z) => z.id === zoneId);
    const zone = zoneFromStore || zoneFromSelection;

    if (zone) {
      const geometry = (zone as any)?.geometry?.geom || (zone as any)?.geometry;
      set((state) => ({
        selectedZone: zone,
        zoneGeometryById: geometry
          ? {
              ...state.zoneGeometryById,
              [zoneId]: geometry,
            }
          : state.zoneGeometryById,
      }));
      return;
    }

    authorizedAPI
      .get(`/utm/zones/${zoneId}`)
      .then((res) => {
        const fetchedZone = res.data || null;
        const geometry = fetchedZone?.geometry?.geom || fetchedZone?.geometry;
        set((state) => ({
          selectedZone: fetchedZone,
          zoneGeometryById: fetchedZone && geometry
            ? {
                ...state.zoneGeometryById,
                [zoneId]: geometry,
              }
            : state.zoneGeometryById,
        }));
      })
      .catch((error) => {
        console.error('Failed to fetch UTM zone details:', error);
        set({ selectedZone: null });
      });
  },

  // Impacts
  fetchImpacts: async () => {
    set({ isLoading: true });
    try {
      const res = await authorizedAPI.get('/utm/impacts');
      set({ impacts: res.data, isLoading: false });
    } catch (error) {
      console.error('Failed to fetch UTM impacts:', error);
      set({ isLoading: false });
    }
  },

  createImpact: async (impact: Partial<Impact>) => {
    set({ isLoading: true });
    try {
      await authorizedAPI.post('/utm/impacts', impact);
      await get().fetchImpacts();
    } catch (error) {
      console.error('Failed to create UTM impact:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  updateImpact: async (id: string, updates: Partial<Impact>) => {
    try {
      await authorizedAPI.patch(`/utm/impacts/${id}`, updates);
      await get().fetchImpacts();
    } catch (error) {
      console.error('Failed to update UTM impact:', error);
      throw error;
    }
  },

  deleteImpact: async (id: string) => {
    set({ isLoading: true });
    try {
      await authorizedAPI.delete(`/utm/impacts/${id}`);
      await get().fetchImpacts();
    } catch (error) {
      console.error('Failed to delete UTM impact:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  attachImpact: async (zoneId: string, impactId: string) => {
    try {
      await authorizedAPI.post(`/utm/zones/${zoneId}/impacts/${impactId}`);
      await get().fetchZones();
    } catch (error) {
      console.error('Failed to attach UTM impact:', error);
      throw error;
    }
  },

  detachImpact: async (zoneId: string, impactId: string) => {
    try {
      await authorizedAPI.delete(`/utm/zones/${zoneId}/impacts/${impactId}`);
      await get().fetchZones();
    } catch (error) {
      console.error('Failed to detach UTM impact:', error);
      throw error;
    }
  },

  // Impact-Zone Linking
  fetchImpactZones: async (impactId: string, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.search) params.append('search', filters.search);
      if (filters.groupCode) params.append('groupCode', filters.groupCode);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.status) params.append('status', filters.status);

      const res = await authorizedAPI.get(`/utm/impacts/${impactId}/zones/linked?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch UTM impact zones:', error);
      throw error;
    }
  },

  fetchAvailableZones: async (impactId: string, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.search) params.append('search', filters.search);
      if (filters.groupCode) params.append('groupCode', filters.groupCode);
      if (filters.categoryId) params.append('categoryId', filters.categoryId);
      if (filters.status) params.append('status', filters.status);

      const res = await authorizedAPI.get(`/utm/impacts/${impactId}/zones/available?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch UTM available zones:', error);
      throw error;
    }
  },

  attachZoneToImpact: async (impactId: string, zoneId: string) => {
    try {
      await authorizedAPI.post(`/utm/impacts/${impactId}/zones/${zoneId}`);
    } catch (error) {
      console.error('Failed to attach zone to UTM impact:', error);
      throw error;
    }
  },

  detachZoneFromImpact: async (impactId: string, zoneId: string) => {
    try {
      await authorizedAPI.delete(`/utm/impacts/${impactId}/zones/${zoneId}`);
    } catch (error) {
      console.error('Failed to detach zone from UTM impact:', error);
      throw error;
    }
  },

  // Analysis
  queryIntersections: async (geometry: any, filters = {}) => {
    set({ isLoading: true });
    try {
      const {
        drawingElevation,
        activeSelectionFeatureId,
        selectionResultsByFeatureId,
        intersectionZoneIdsByFeatureId,
        visibleIntersectionZoneIdsByFeatureId,
      } = get();
      const allFilters = { ...filters, ...drawingElevation };
      const res = await authorizedAPI.post('/utm/analysis/intersections', {
        geometry,
        filters: allFilters,
      });
      const nextZoneIds = Array.isArray(res.data?.zones)
        ? res.data.zones.map((zone: Zone) => zone.id)
        : [];
      const nextResultsByFeatureId = activeSelectionFeatureId
        ? {
            ...selectionResultsByFeatureId,
            [activeSelectionFeatureId]: res.data,
          }
        : selectionResultsByFeatureId;
      const nextIntersectionZoneIdsByFeatureId = activeSelectionFeatureId
        ? {
            ...intersectionZoneIdsByFeatureId,
            [activeSelectionFeatureId]: nextZoneIds,
          }
        : intersectionZoneIdsByFeatureId;
      const nextVisibleIntersectionZoneIdsByFeatureId = (() => {
        if (!activeSelectionFeatureId) return visibleIntersectionZoneIdsByFeatureId;
        const previousAll = intersectionZoneIdsByFeatureId[activeSelectionFeatureId] ?? [];
        const previousVisible = new Set(
          visibleIntersectionZoneIdsByFeatureId[activeSelectionFeatureId] ?? [],
        );
        const hasPreviousAll = previousAll.length > 0;
        const previousAllSet = new Set(previousAll);
        const nextVisible = hasPreviousAll
          ? nextZoneIds.filter(
              (zoneId: string) => !previousAllSet.has(zoneId) || previousVisible.has(zoneId),
            )
          : nextZoneIds;

        return {
          ...visibleIntersectionZoneIdsByFeatureId,
          [activeSelectionFeatureId]: nextVisible,
        };
      })();
      set({
        selectionResults: res.data,
        selectionResultsByFeatureId: nextResultsByFeatureId,
        intersectionZoneIdsByFeatureId: nextIntersectionZoneIdsByFeatureId,
        visibleIntersectionZoneIdsByFeatureId: nextVisibleIntersectionZoneIdsByFeatureId,
        isLoading: false,
      });
      return res.data as IntersectionResult;
    } catch (error) {
      console.error('Failed to query UTM intersections:', error);
      set({ isLoading: false });
      return null;
    }
  },

  setSelectionMode: (mode) => set({ selectionMode: mode }),

  addSelectionFeature: async (geometry: any) => {
    if (!geometry) return;
    const newFeature: SelectionFeature = {
      id: crypto.randomUUID(),
      geometry,
    };

    const nextFeatures = [...get().selectionFeatures, newFeature];
    const combinedGeometry = combineSelectionGeometries(nextFeatures);

    set({
      selectionFeatures: nextFeatures,
      activeSelectionFeatureId: newFeature.id,
      drawingElevation: {},
      hoveredZoneIds: [],
      selectionElevationByFeatureId: {
        ...get().selectionElevationByFeatureId,
        [newFeature.id]: {},
      },
      currentDrawingGeometry: combinedGeometry,
    });
    get().updateDrawingCalculations(combinedGeometry);

    const result = await get().queryIntersections(geometry, {
      status: 'PUBLISHED',
      activeNow: true,
    });

    if (result) {
      set((state) => ({
        selectionResultsByFeatureId: {
          ...state.selectionResultsByFeatureId,
          [newFeature.id]: result,
        },
        intersectionZoneIdsByFeatureId: {
          ...state.intersectionZoneIdsByFeatureId,
          [newFeature.id]: result.zones.map((zone) => zone.id),
        },
        visibleIntersectionZoneIdsByFeatureId: {
          ...state.visibleIntersectionZoneIdsByFeatureId,
          [newFeature.id]: result.zones.map((zone) => zone.id),
        },
        selectionResults: result,
      }));
    }
  },

  setActiveSelectionFeature: (id: string | null) => {
    const state = get();
    const selectedFeature = id ? state.selectionFeatures.find((feature) => feature.id === id) : null;
    const nextDrawingElevation = id ? state.selectionElevationByFeatureId[id] ?? {} : {};
    set({
      activeSelectionFeatureId: id,
      selectionResults: id ? state.selectionResultsByFeatureId[id] ?? null : null,
      drawingElevation: nextDrawingElevation,
      hoveredZoneIds: [],
    });
    if (selectedFeature) {
      void get().queryIntersections(selectedFeature.geometry, {
        status: 'PUBLISHED',
        activeNow: true,
      });
    }
  },

  removeActiveSelectionFeature: async () => {
    const { activeSelectionFeatureId, selectionFeatures } = get();
    if (!activeSelectionFeatureId || selectionFeatures.length === 0) return;

    const nextFeatures = selectionFeatures.filter((feature) => feature.id !== activeSelectionFeatureId);
    const nextActiveId = nextFeatures.length > 0 ? nextFeatures[nextFeatures.length - 1].id : null;
    const combinedGeometry = combineSelectionGeometries(nextFeatures);
    const nextResultsByFeatureId = { ...get().selectionResultsByFeatureId };
    const nextElevationByFeatureId = { ...get().selectionElevationByFeatureId };
    const nextIntersectionZoneIdsByFeatureId = { ...get().intersectionZoneIdsByFeatureId };
    const nextVisibleIntersectionZoneIdsByFeatureId = {
      ...get().visibleIntersectionZoneIdsByFeatureId,
    };
    delete nextResultsByFeatureId[activeSelectionFeatureId];
    delete nextElevationByFeatureId[activeSelectionFeatureId];
    delete nextIntersectionZoneIdsByFeatureId[activeSelectionFeatureId];
    delete nextVisibleIntersectionZoneIdsByFeatureId[activeSelectionFeatureId];
    const nextDrawingElevation = nextActiveId ? nextElevationByFeatureId[nextActiveId] ?? {} : {};

    set({
      selectionFeatures: nextFeatures,
      activeSelectionFeatureId: nextActiveId,
      selectionResultsByFeatureId: nextResultsByFeatureId,
      selectionElevationByFeatureId: nextElevationByFeatureId,
      intersectionZoneIdsByFeatureId: nextIntersectionZoneIdsByFeatureId,
      visibleIntersectionZoneIdsByFeatureId: nextVisibleIntersectionZoneIdsByFeatureId,
      selectionResults: nextActiveId ? nextResultsByFeatureId[nextActiveId] ?? null : null,
      drawingElevation: nextDrawingElevation,
      currentDrawingGeometry: combinedGeometry,
      hoveredZoneIds: [],
    });
    get().updateDrawingCalculations(combinedGeometry);
    if (!combinedGeometry) {
      set({
        selectionResults: null,
        selectedZone: null,
      });
    }
  },

  clearSelection: () =>
    set({
      selectionResults: null,
      selectionMode: 'none',
      selectionFeatures: [],
      activeSelectionFeatureId: null,
      selectionResultsByFeatureId: {},
      selectionElevationByFeatureId: {},
      intersectionZoneIdsByFeatureId: {},
      visibleIntersectionZoneIdsByFeatureId: {},
      hoveredZoneIds: [],
      selectedZone: null,
      currentDrawingGeometry: null,
      currentDrawingArea: 0,
      currentDrawingRadius: null,
    }),

  setIntersectionZoneVisible: (zoneId: string, visible: boolean) => {
    const { activeSelectionFeatureId } = get();
    if (!activeSelectionFeatureId) return;

    set((state) => {
      const currentVisible = new Set(
        state.visibleIntersectionZoneIdsByFeatureId[activeSelectionFeatureId] ?? [],
      );
      if (visible) {
        currentVisible.add(zoneId);
      } else {
        currentVisible.delete(zoneId);
      }

      return {
        visibleIntersectionZoneIdsByFeatureId: {
          ...state.visibleIntersectionZoneIdsByFeatureId,
          [activeSelectionFeatureId]: Array.from(currentVisible),
        },
      };
    });
  },

  setHoveredZoneIds: (zoneIds: string[]) => {
    set({ hoveredZoneIds: Array.from(new Set(zoneIds.filter(Boolean))) });
  },

  ensureZoneGeometries: async (zoneIds: string[]) => {
    if (!Array.isArray(zoneIds) || zoneIds.length === 0) return;

    const uniqueIds = Array.from(new Set(zoneIds));
    const { zoneGeometryById } = get();
    const missingIds = uniqueIds.filter((zoneId) => !zoneGeometryById[zoneId]);
    if (missingIds.length === 0) return;

    const settled = await Promise.allSettled(
      missingIds.map((zoneId) => authorizedAPI.get(`/utm/zones/${zoneId}`)),
    );

    const nextGeometryEntries: ZoneGeometryMap = {};
    settled.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const zone = result.value?.data as Zone | undefined;
      const geometry = (zone as any)?.geometry?.geom || (zone as any)?.geometry;
      if (!geometry) return;
      nextGeometryEntries[missingIds[index]] = geometry;
    });

    if (Object.keys(nextGeometryEntries).length > 0) {
      set((state) => ({
        zoneGeometryById: {
          ...state.zoneGeometryById,
          ...nextGeometryEntries,
        },
      }));
    }
  },

  // Drawing State Actions
  setDrawingElevation: (elevation) => {
    const { activeSelectionFeatureId, selectionFeatures } = get();
    const nextDrawingElevation = { ...get().drawingElevation, ...elevation };

    set((state) => ({
      drawingElevation: nextDrawingElevation,
      selectionElevationByFeatureId: activeSelectionFeatureId
        ? {
            ...state.selectionElevationByFeatureId,
            [activeSelectionFeatureId]: nextDrawingElevation,
          }
        : state.selectionElevationByFeatureId,
    }));

    if (!activeSelectionFeatureId) return;
    const activeFeature = selectionFeatures.find((feature) => feature.id === activeSelectionFeatureId);
    if (!activeFeature) return;

    void get().queryIntersections(activeFeature.geometry, {
      status: 'PUBLISHED',
      activeNow: true,
    });
  },

  setCorridorWidth: (width) => set({ corridorWidth: width }),

  setCurrentDrawingGeometry: (geometry) => {
    set({ currentDrawingGeometry: geometry });
    get().updateDrawingCalculations(geometry);
  },

  updateDrawingCalculations: (geometry) => {
    if (!geometry) {
      set({ currentDrawingArea: 0, currentDrawingRadius: null });
      return;
    }
    try {
      const area = calculateArea(geometry);
      const radius = calculateRadius(geometry);
      set({ currentDrawingArea: area, currentDrawingRadius: radius });
    } catch (error) {
      console.error('Error calculating UTM geometry:', error);
      set({ currentDrawingArea: 0, currentDrawingRadius: null });
    }
  },

  // Utilities
  getEffectiveStyle: (groupCode: string) => {
    const group = get().groups.find((g) => g.code === groupCode);
    if (!group) return {};
    const category = get().categories.find((c) => c.id === group.categoryId);
    if (!category) return group.style || {};
    return {
      ...category.style,
      ...group.style,
      polygon: { ...category.style?.polygon, ...group.style?.polygon },
      line: { ...category.style?.line, ...group.style?.line },
      point: { ...category.style?.point, ...group.style?.point },
      label: { ...category.style?.label, ...group.style?.label },
    };
  },

  setBasemapStyle: (style) => set({ basemapStyle: style }),
}));
